import React from 'react';
import classNames from 'classnames';
import { AR, PaintSwatches, Play, SpecialSavings, ThreeSixtySpin } from '@one-thd/sui-icons';
import Image from '../../common/Image';
import { thumbnailPropTypes } from '../propTypes';
import { getButtonTitle, getMediaEventProps, getThumbnailMeta } from '../utils/mediaGalleryUtils';
import ThumbnailOverlay from './ThumbnailOverlay';
import { THUMBNAIL_OVERLAY_COLOR } from '../constants';

const Thumbnail = ({
  thumbnail = {},
  thumbnailIndex = 0,
  totalThumbnailsToShow = 0,
  thumbnailCount = 0,
  availableThumbnails = [],
  mainImageList = [],
  onThumbnailEvent = () => {},
  thumbnailSize = 'md',
  grid = false,
  noPipSpacing = false
}) => {
  const meta = getThumbnailMeta(thumbnail, thumbnailIndex, totalThumbnailsToShow);
  const isSelected = thumbnail?.isSelected;
  const isLast = meta.isLastThumbnail;
  const countLabel = `+${thumbnailCount - (totalThumbnailsToShow - 1)}`;
  const showCount = isLast && thumbnailCount > totalThumbnailsToShow;
  const isInteractiveMedia = meta.isVideo || meta.isAR || meta.isThreesixty
                            || meta.isHotspot || meta.isCustomColor || meta.isManualColor;

  const mediaProps = getMediaEventProps(
    grid,
    onThumbnailEvent,
    availableThumbnails,
    mainImageList,
    thumbnail
  );

  return (
    <div
      className={classNames(
        'mediagallery__thumbnail sui-relative sui-aspect-square sui-overflow-hidden sui-box-border sui-border-2',
        { 'mediagallery__thumbnail--no-spacing': noPipSpacing }
      )}
    >
      <button
        {...mediaProps}
        type="button"
        className="sui-flex sui-justify-center sui-items-center sui-w-full sui-h-full sui-relative"
        title={getButtonTitle(isLast, isInteractiveMedia, thumbnail, thumbnailCount)}
      >
        {isSelected && (
          <div
            className="sui-absolute sui-inset-0 sui-pointer-events-none
              sui-border-2 sui-border-solid sui-border-stronger"
          />
        )}
        {meta.isManualColor && (
          <div className="sui-bg-inactive sui-flex sui-items-center sui-justify-center sui-h-full">
            <div className="sui-text-center sui-flex sui-items-center sui-justify-center">
              <PaintSwatches color="medium" size="large" />
              <span className="sui-absolute sui-inset-0 sui-bg-black sui-opacity-50" />
            </div>
          </div>
        )}
        {meta.isCustomColor ? (
          <div
            className="sui-h-full sui-flex sui-items-center sui-justify-center"
            style={{
              backgroundColor: `rgb(${meta.rgb?.red},${meta.rgb?.green},${meta.rgb?.blue})`
            }}
          >
            <Image
              src={meta.brandLogo}
              alt={meta.alt}
              lazy
              className="sui-w-1/2 sui-h-1/2 sui-object-contain"
              data-testid="thumbnail-image"
              data-lazy="true"
            />
          </div>
        ) : (
          <>
            <Image
              src={
                meta.isImagePath
                  ? meta.finalReducedThumbnailUrl
                  : meta.finalThumbnailUrl
              }
              alt={meta.alt || ''}
              {...(grid && { height: '88', width: '88' })}
              fetchPriority="low"
              lazy
              data-testid="thumbnail-image"
              data-lazy="true"
            />

            {thumbnailSize !== 'lg' && (!isLast || (isLast && !showCount)) && (
              <>
                {meta.isHotspot && (
                  <ThumbnailOverlay
                    icon={SpecialSavings}
                    iconProps={{ 'data-testid': 'hotspot-tag-overlay' }}
                  />
                )}
                {meta.isAR && meta.isAvataarAR && <ThumbnailOverlay icon={AR} />}
                {meta.isAR && !meta.isAvataarAR && <ThumbnailOverlay icon={AR} />}
                {meta.isVideo && <ThumbnailOverlay icon={Play} />}
                {meta.isThreesixty && <ThumbnailOverlay icon={ThreeSixtySpin} />}
              </>
            )}

            {showCount && (
              <span
                className="sui-absolute sui-inset-0 sui-flex sui-items-center sui-justify-center sui-text-inverse"
                style={{ backgroundColor: THUMBNAIL_OVERLAY_COLOR, zIndex: 5 }}
                data-testid="thumbnail-count-overlay"
              >
                <span className="sui-text-inverse sui-text-xl sui-font-bold">
                  {countLabel}
                </span>
              </span>
            )}
          </>
        )}
      </button>
    </div>
  );
};

Thumbnail.propTypes = thumbnailPropTypes;

export default Thumbnail;
